/*
 * <lurk-gallery> — a scroll-snap media gallery.
 *
 * The track is a plain overflow-x scroller, so swipe, trackpad and keyboard
 * scrolling all work with no JS at all. This element only adds the affordances
 * that need state: prev/next arrows, thumbnails, dots, and keeping them in sync
 * with wherever the track has been scrolled to.
 */
class LurkGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    if (!this.viewport || this.slides.length < 2) return;

    this.goers = Array.from(this.querySelectorAll('[data-gallery-go]'));
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.index = 0;

    this.onScroll = this.onScroll.bind(this);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.go(this.index - 1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.go(this.index + 1));
    }
    this.goers.forEach((el) => {
      el.addEventListener('click', () => this.go(Number(el.dataset.galleryGo)));
    });

    this.sync(0);
  }

  disconnectedCallback() {
    if (this.viewport) this.viewport.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.settleTimer);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  go(target) {
    const index = Math.max(0, Math.min(target, this.slides.length - 1));
    this.viewport.scrollTo({
      left: this.slides[index].offsetLeft - this.slides[0].offsetLeft,
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
    });
    // Update chrome immediately; the scroll handler will confirm on settle.
    this.sync(index);
  }

  onScroll() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      const origin = this.slides[0].offsetLeft;
      const scrolled = this.viewport.scrollLeft;
      let nearest = 0;
      let shortest = Infinity;

      this.slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - origin - scrolled);
        if (distance < shortest) {
          shortest = distance;
          nearest = i;
        }
      });

      this.sync(nearest);
    }, 80);
  }

  sync(index) {
    this.index = index;

    // The frame takes the current image's aspect ratio so it fills edge to
    // edge. Server-rendered from the first image, so this only ever changes it.
    const slide = this.slides[index];
    if (slide && slide.dataset.ratio) {
      this.style.setProperty('--cp-ratio', slide.dataset.ratio);
      this.style.setProperty('--cp-ratio-num', slide.dataset.ratioNum);
    }

    this.goers.forEach((el) => {
      const isCurrent = Number(el.dataset.galleryGo) === index;
      el.classList.toggle('is-active', isCurrent);
      el.setAttribute('aria-current', isCurrent ? 'true' : 'false');
    });

    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
  }
}

if (!customElements.get('lurk-gallery')) {
  customElements.define('lurk-gallery', LurkGallery);
}
